import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Archive, Download, Loader2, RefreshCw, Play } from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin;

export default function BackupPanel() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dlId, setDlId] = useState(null);

  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/backups`, { headers });
      setBackups(data.backups || []);
    } catch (e) { toast.error("Yedek listesi alınamadı"); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/backups/run`, {}, { headers });
      toast.success(`Yedek alındı: ${data.backup_date} (${data.booking_count} rez)`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Yedek alınamadı"); }
    finally { setBusy(false); }
  };

  const download = async (b) => {
    setDlId(b.id);
    try {
      const { data } = await axios.get(`${API}/api/backups/${b.id}`, { headers });
      const blob = new Blob([JSON.stringify(data.payload || data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `LuferYedek_${b.backup_date}_${b.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Yedek indirildi");
    } catch (e) { toast.error("İndirilemedi"); }
    finally { setDlId(null); }
  };

  const fmt = (ts) => { try { return new Date(ts).toLocaleString("tr-TR"); } catch { return ts; } };

  return (
    <div data-testid="backup-panel-page">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center">
            <Archive className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white leading-none">Yedekler</h1>
            <p className="text-[11px] text-slate-400 mt-1">Her gün TR saatiyle 19:00'da otomatik yedek alınır</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="backup-refresh" onClick={load}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-medium px-3 py-2 rounded-lg transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Yenile
          </button>
          <button data-testid="backup-run-now" onClick={runNow} disabled={busy}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Şimdi Yedekle
          </button>
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="text-center text-slate-400 py-10 text-sm">Yükleniyor…</div>
        ) : backups.length === 0 ? (
          <div className="text-center text-slate-500 py-10 text-sm">Henüz yedek yok. "Şimdi Yedekle" ile ilk yedeği alabilirsiniz.</div>
        ) : (
          <div className="max-w-3xl space-y-1.5" data-testid="backup-list">
            {backups.map((b) => (
              <div key={b.id} data-testid={`backup-row-${b.id}`}
                className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-lg px-4 py-3">
                <div>
                  <div className="text-sm text-slate-100 font-semibold">{b.backup_date}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {fmt(b.created_at)} · {b.booking_count} rez · {b.op_count} satır
                  </div>
                </div>
                <button data-testid={`backup-download-${b.id}`} onClick={() => download(b)} disabled={dlId === b.id}
                  className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-slate-200 text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
                  {dlId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} İndir
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
